import { useCallback } from "react";
import { getItemPopupList } from "src/apis/itemApi";
import { showWarning } from "src/utils/toast/toastUtil";
import { validateItemAccount, isDuplicateChildItemcd, restoreOriginalValues } from "../Util_KSK2090_R01";
import { KSK2090_R01DetailDto } from "../Dto_KSK2090_R01";
import { getBomChildItemList } from "src/apis/bomApi";
import { getAfeederCodeList, getBfeederCodeList, getCfeederCodeList } from "src/apis/commonCodeApi";

export function useCoordinateCellChange({
	KSK2090_R01DetailList,
	updateKSK2090_R01DetailList,
	originalKSK2090_R01DetailList,
	settingCodeSearchModalProps,
	gridRef
}: any) {
	// 자품목코드 자동 검색 함수
	const handleChildItemCodeSearch = useCallback(
		async (itemCode: string, rowData: KSK2090_R01DetailDto, oldValue?: string) => {
			if (!itemCode || !itemCode.trim())			return;
			const originalData = {
				...rowData,
				childItemcd:		oldValue || rowData.childItemcd,
				childItemnm:		rowData.childItemnm,
			};
			const prntItemcd = rowData.prntItemcd;
			try {
				const response	= await getBomChildItemList(prntItemcd);
				settingCodeSearchModalProps(
					'BOM_자품목',
					prntItemcd,
					false,
					'childItemcd',
					rowData,
					rowData.valueIndex!,
				);
			} catch (error) {
				console.error('품목 검색 중 오류:', error);
				restoreOriginalValues(rowData.valueIndex!, originalData, updateKSK2090_R01DetailList, gridRef);
				settingCodeSearchModalProps(
					'BOM_자품목',
					prntItemcd || 'all',
					false,
					'childItemcd',
					rowData,
					rowData.valueIndex!,
				);
			}
		},
		[
			KSK2090_R01DetailList,
			updateKSK2090_R01DetailList,
			settingCodeSearchModalProps,
			restoreOriginalValues,
			gridRef
		]
	);

	const handleAfeederCodeSearch = useCallback(
		async (itemCode: string, rowData: KSK2090_R01DetailDto, oldValue?: string) => {
			if (!itemCode || !itemCode.trim())			return;
			const originalData = {
				...rowData,
				afeederCd:		itemCode,
			};
			try {
				const response	= await getAfeederCodeList();
				settingCodeSearchModalProps(
					'Afeeder',
					itemCode,
					false,
					'afeederCd',
					rowData,
					rowData.valueIndex!,
				);
			} catch (error) {
				console.error('품목 검색 중 오류:', error);
				restoreOriginalValues(rowData.valueIndex!, originalData, updateKSK2090_R01DetailList, gridRef);
				settingCodeSearchModalProps(
					'Afeeder',
					itemCode,
					false,
					'afeederCd',
					rowData,
					rowData.valueIndex!,
				);
			}
		},
		[
			KSK2090_R01DetailList,
			updateKSK2090_R01DetailList,
			settingCodeSearchModalProps,
			restoreOriginalValues,
			gridRef
		]
	);

	const handleBfeederCodeSearch = useCallback(
		async (itemCode: string, rowData: KSK2090_R01DetailDto, oldValue?: string) => {
			if (!itemCode || !itemCode.trim())			return;
			const originalData = {
				...rowData,
				bfeederCd:		itemCode,
			};
			try {
				const response	= await getBfeederCodeList();
				settingCodeSearchModalProps(
					'Bfeeder',
					itemCode,
					false,
					'bfederCd',
					rowData,
					rowData.valueIndex!,
				);
			} catch (error) {
				console.error('품목 검색 중 오류:', error);
				restoreOriginalValues(rowData.valueIndex!, originalData, updateKSK2090_R01DetailList, gridRef);
				settingCodeSearchModalProps(
					'Bfeeder',
					itemCode,
					false,
					'bfeederCd',
					rowData,
					rowData.valueIndex!,
				);
			}
		},
		[
			KSK2090_R01DetailList,
			updateKSK2090_R01DetailList,
			settingCodeSearchModalProps,
			restoreOriginalValues,
			gridRef
		]
	);

	const handleCfeederCodeSearch = useCallback(
		async (itemCode: string, rowData: KSK2090_R01DetailDto, oldValue?: string) => {
			if (!itemCode || !itemCode.trim())			return;
			const originalData = {
				...rowData,
				cfeederCd:		itemCode,
			};
			try {
				const response	= await getCfeederCodeList();
				settingCodeSearchModalProps(
					'Cfeeder',
					itemCode,
					false,
					'cfeederCd',
					rowData,
					rowData.valueIndex!,
				);
			} catch (error) {
				console.error('품목 검색 중 오류:', error);
				restoreOriginalValues(rowData.valueIndex!, originalData, updateKSK2090_R01DetailList, gridRef);
				settingCodeSearchModalProps(
					'Cfeeder',
					itemCode,
					false,
					'cfeederCd',
					rowData,
					rowData.valueIndex!,
				);
			}
		},
		[
			KSK2090_R01DetailList,
			updateKSK2090_R01DetailList,
			settingCodeSearchModalProps,
			restoreOriginalValues,
			gridRef
		]
	);

	// 커스텀 셀 변경 핸들러
	const handleCellChange = useCallback(
		(event: any) => {
			const { data, colDef, newValue, oldValue } = event;
			if (!data || !colDef.field)	return;
			const field = colDef.field as keyof KSK2090_R01DetailDto;
			if (field === 'childItemcd' && newValue !== oldValue) {
				handleChildItemCodeSearch(newValue, data, oldValue);
				return;
			}
			if (field === 'afeederCd' && newValue !== oldValue) {
				handleAfeederCodeSearch(newValue, data, oldValue);
				return;
			}
			if (field === 'bfeederCd' && newValue !== oldValue) {
				handleBfeederCodeSearch(newValue, data, oldValue);
				return;
			}
			if (field === 'cfeederCd' && newValue !== oldValue) {
				handleCfeederCodeSearch(newValue, data, oldValue);
				return;
			}
			
			const originalData =
				originalKSK2090_R01DetailList.find(
					(item: KSK2090_R01DetailDto) => item.valueIndex === data.valueIndex
				);
			const isModified =
				data.inserted === true
					? false
					: (originalData ? originalData[field] !== newValue : true);
			
			let updatedData = {
				...data,
				[field]: newValue,
				isModified: true
			};
			
			updateKSK2090_R01DetailList(data.valueIndex!, updatedData);
		},
		[
			handleChildItemCodeSearch,
			originalKSK2090_R01DetailList,
			updateKSK2090_R01DetailList
		]
	);

	return {
		handleCellChange,
		handleChildItemCodeSearch
	};
}